import { useState } from 'react'
import { Button, Form, Input, Space, Spin, Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import {
  agendaTemplateKey,
  withDefaultAgendaTemplate,
  type AgendaTemplateOption,
  type SavedAgendaTemplate,
} from '@/features/advisory-ai/agendaTemplates'
import {
  useAdvisoryAgendaTemplates,
  useCreateAdvisoryAgendaTemplateMutation,
  useDeleteAdvisoryAgendaTemplateMutation,
  useUpdateAdvisoryAgendaTemplateMutation,
} from '@/features/advisory-ai/hooks'

type AgendaTemplatesPanelProps = {
  clientId: string
}

type TemplateFormValues = {
  title: string
  items: { title: string; description: string }[]
}

function normalizeTemplateItems(rows: TemplateFormValues['items'] | undefined) {
  return (rows ?? [])
    .map((row) => ({
      title: (row?.title ?? '').trim(),
      description: (row?.description ?? '').trim(),
    }))
    .filter((row) => row.title.length > 0)
}

export function AgendaTemplatesPanel({ clientId }: AgendaTemplatesPanelProps) {
  const { addToast } = useToast()
  const templatesQuery = useAdvisoryAgendaTemplates(clientId)
  const createMutation = useCreateAdvisoryAgendaTemplateMutation(clientId)
  const updateMutation = useUpdateAdvisoryAgendaTemplateMutation(clientId)
  const deleteMutation = useDeleteAdvisoryAgendaTemplateMutation(clientId)
  const [editingTemplate, setEditingTemplate] = useState<AgendaTemplateOption | null>(null)
  const [form] = Form.useForm<TemplateFormValues>()

  const handleAddTemplate = () => {
    setEditingTemplate(null)
    form.resetFields()
    form.setFieldsValue({
      title: '',
      items: [{ title: '', description: '' }],
    })
  }

  const handleEditTemplate = (template: AgendaTemplateOption) => {
    setEditingTemplate(template)
    form.setFieldsValue({
      title: template.title,
      items:
        template.items.length > 0
          ? template.items.map((line) => ({ title: line.title, description: line.description }))
          : [{ title: '', description: '' }],
    })
  }

  const handleDeleteTemplate = async (template: SavedAgendaTemplate) => {
    try {
      await deleteMutation.mutateAsync(template.advisoryAgendaTemplateId)
      addToast('Template deleted', 'success')
      if (editingTemplate && agendaTemplateKey(editingTemplate) === agendaTemplateKey(template)) {
        setEditingTemplate(null)
        form.resetFields()
      }
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to delete', 'error')
    }
  }

  const handleTemplateSubmit = async (values: TemplateFormValues) => {
    const items = normalizeTemplateItems(values.items)
    try {
      if (editingTemplate?.kind === 'saved') {
        await updateMutation.mutateAsync({
          templateId: editingTemplate.advisoryAgendaTemplateId,
          clientId,
          title: values.title,
          items,
        })
        addToast('Template updated', 'success')
      } else {
        await createMutation.mutateAsync({
          clientId,
          title: values.title,
          items,
        })
        addToast('Template created', 'success')
      }
      setEditingTemplate(null)
      form.resetFields()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save', 'error')
    }
  }

  const templates = withDefaultAgendaTemplate(templatesQuery.data ?? [])
  // Without the saved list we can't tell whether the default was already saved, so saving it could duplicate it.
  const canSave = !(editingTemplate?.kind === 'default' && !templatesQuery.data)

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-4 border border-gray-200 rounded-lg bg-white p-4">
        <div className="flex items-center justify-between">
          <Space>
            <span className="font-medium text-gray-900">Agenda Templates</span>
            <Tooltip title="Manage reusable agenda templates">
              <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTemplate} className="feature-button-solid">
            Add Template
          </Button>
        </div>

        {templatesQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-3">
            {templatesQuery.error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                {templatesQuery.error instanceof Error ? templatesQuery.error.message : 'Failed to load templates'}
              </div>
            ) : null}
            {templates.map((template) => {
              const lineCount = template.items.filter((l) => l.title.trim()).length
              const preview = template.items.find((l) => l.title.trim())?.title
              return (
                <div
                  key={agendaTemplateKey(template)}
                  className={`flex items-start gap-3 rounded-lg border p-4 ${
                    editingTemplate && agendaTemplateKey(editingTemplate) === agendaTemplateKey(template)
                      ? 'feature-panel-soft'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 truncate">{template.title}</h3>
                      {template.kind === 'default' && (
                        <span className="feature-panel-soft feature-label-accent text-xs font-medium rounded-full px-2 py-0.5 shrink-0">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {lineCount === 0
                        ? 'No agenda lines'
                        : `${lineCount} agenda line${lineCount === 1 ? '' : 's'}${preview ? ` · ${preview}` : ''}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditTemplate(template)}
                      className="text-gray-500 feature-link"
                    />
                    {template.kind === 'saved' && (
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteTemplate(template)}
                        loading={deleteMutation.isPending}
                        className="text-gray-500 hover:text-red-600"
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 bg-gray-50/70 flex items-center justify-between">
          <h2 className="font-medium text-gray-900">{editingTemplate ? 'Edit template' : 'Add template'}</h2>
          {editingTemplate ? (
            <span className="feature-panel-soft feature-label-accent text-xs font-medium rounded-full px-2 py-0.5">
              Editing
            </span>
          ) : null}
        </div>
        <Form form={form} layout="vertical" size="large" onFinish={handleTemplateSubmit} className="p-5">
          <Form.Item
            name="title"
            label="Template name"
            rules={[{ required: true, message: 'Enter a template name' }]}
            className="mb-5"
          >
            <Input placeholder="e.g. Monthly financial review" className="rounded-md" />
          </Form.Item>

          <div className="text-sm font-medium text-gray-700 mb-3">Agenda lines</div>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <div className="space-y-3">
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-gray-200 bg-gray-50/40 p-2.5"
                  >
                    <Form.Item {...restField} name={[name, 'title']} className="mb-0">
                      <Input placeholder="Agenda title" />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'description']} className="mb-0">
                      <Input placeholder="Description (optional)" />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                      className="h-8 w-8 min-w-8 p-0 flex items-center justify-center"
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ title: '', description: '' })}
                  block
                  icon={<PlusOutlined />}
                >
                  Add agenda line
                </Button>
              </div>
            )}
          </Form.List>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="min-w-20"
              onClick={() => {
                setEditingTemplate(null)
                form.resetFields()
              }}
            >
              Clear
            </Button>
            <Button
              type="primary"
              className="feature-button-solid"
              htmlType="submit"
              disabled={!canSave}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editingTemplate ? 'Save' : 'Add'}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  )
}
